import { createConsumer, Consumer } from '@rails/actioncable';
import { API_CONFIG } from '../config/apiConfig';
import { getAuthToken } from '../utils/firebase';

/**
 * Single owner of the shared ActionCable consumer.
 *
 * Lifecycle (see AuthContext, which drives it):
 *   authenticated → getCableConsumer() lazily creates ONE consumer
 *   feature channels (FriendChannel, GameChannel, SessionChannel) share it
 *   GameScreen mount/unmount NEVER disconnects it (F1)
 *   logout / account switch → disconnectCable() tears it down centrally (F2)
 *
 * Concurrency & identity guarantees:
 *   - Concurrent getCableConsumer() callers converge on ONE in-flight creation
 *     (F3): the first caller starts creation and every other caller awaits the
 *     same promise, so exactly one Consumer (one WebSocket) is ever created.
 *   - A disconnect (logout/switch) that happens WHILE a creation is awaiting the
 *     Firebase token invalidates that creation via an epoch guard, so a stale
 *     consumer for the previous identity can never be resurrected afterwards.
 */

let consumer: Consumer | null = null;
let cachedToken: string | null = null;
let tokenRefreshTimer: ReturnType<typeof setInterval> | null = null;

// A single in-flight creation shared by all concurrent callers (F3).
let creationPromise: Promise<Consumer> | null = null;

// Bumped by every disconnectCable(). An in-flight creation captures the epoch
// at its start; if the epoch changes before the consumer is assigned, the
// creation aborts instead of resurrecting a consumer for a logged-out identity.
let epoch = 0;

// How often to proactively refresh the cached token.
// Firebase tokens expire after 1 hour; refresh at 50 minutes to stay ahead.
const TOKEN_REFRESH_INTERVAL_MS = 50 * 60 * 1000;

function ensureTokenRefreshTimer(): void {
  if (tokenRefreshTimer) return;
  tokenRefreshTimer = setInterval(async () => {
    try {
      const token = await getAuthToken();
      // A disconnect/logout may have torn down the consumer while we awaited the
      // new token — do not revive a token for a consumer that no longer exists.
      if (!consumer) return;
      cachedToken = token;
      console.log('[CableClient] Auth token refreshed');
    } catch (e) {
      console.error('[CableClient] Failed to refresh token:', e);
    }
  }, TOKEN_REFRESH_INTERVAL_MS);
}

/**
 * Get or create the ActionCable consumer singleton.
 *
 * Uses a URL *function* (not a string) so ActionCable evaluates it on every
 * reconnect attempt — ensuring a fresh, valid Firebase token is always used.
 * A background timer refreshes the cached token every 50 minutes so it never
 * goes stale between reconnects.
 */
export function getCableConsumer(): Promise<Consumer> {
  if (consumer) return Promise.resolve(consumer);
  // Coalesce concurrent callers onto one creation (F3).
  if (creationPromise) return creationPromise;

  const myEpoch = epoch;
  const p = (async (): Promise<Consumer> => {
    // Fetch the token first (async). This is the window a logout can race with.
    const token = await getAuthToken();
    if (!token) {
      throw new Error('No auth token available for WebSocket connection');
    }
    // disconnectCable() ran while we were awaiting the token — abort so we never
    // resurrect a consumer for an identity that has since logged out (F2).
    if (myEpoch !== epoch) {
      throw new Error('Cable consumer creation aborted — disconnected during setup');
    }

    cachedToken = token;
    ensureTokenRefreshTimer();

    // Pass a URL function — ActionCable calls this on every (re)connect attempt.
    // The @rails/actioncable TS types only declare string, but the runtime
    // supports a function; cast to satisfy the compiler.
    const urlFn = () => {
      const t = cachedToken ?? '';
      const wsUrl = `${API_CONFIG.wsUrl}?token=${encodeURIComponent(t)}`;
      console.log('[WS] Connecting to:', wsUrl.replace(/token=[^&]+/, 'token=<redacted>'));
      return wsUrl;
    };
    consumer = createConsumer(urlFn as unknown as string);
    return consumer;
  })();

  creationPromise = p;
  // Clear the in-flight marker once settled — but only if it is still ours
  // (a disconnect may have already cleared/replaced it).
  const clearIfCurrent = () => { if (creationPromise === p) creationPromise = null; };
  void p.then(clearIfCurrent, clearIfCurrent);
  return p;
}

/**
 * Disconnect the current consumer and reset all state.
 *
 * Called centrally on logout / account switch (see AuthContext) — NOT by
 * GameScreen. Bumps the epoch so any in-flight getCableConsumer() creation is
 * invalidated and cannot resurrect a stale consumer afterwards.
 */
export function disconnectCable(): void {
  // Invalidate any in-flight creation (F2/F3).
  epoch++;
  creationPromise = null;
  if (tokenRefreshTimer) {
    clearInterval(tokenRefreshTimer);
    tokenRefreshTimer = null;
  }
  if (consumer) {
    consumer.disconnect();
    consumer = null;
  }
  cachedToken = null;
}

/**
 * Reconnect with a fresh token (e.g. after a sign-in change).
 */
export async function reconnectCable(): Promise<Consumer> {
  disconnectCable();
  return getCableConsumer();
}
